/**
 * WISD: entrada de las tarjetas de producto al hacer scroll.
 *
 * Revela con opacidad y desplazamiento las tarjetas de las rejillas de productos
 * (lista de productos de la home, colección y búsqueda) cuando entran en pantalla.
 * Las tarjetas que ya se ven al cargar no se tocan: no retrasa el LCP ni mueve el layout.
 * No actúa si el usuario pidió reducir movimiento, en el editor de temas o sin IntersectionObserver.
 *
 * Estilos: snippets/wisd-card-reveal.liquid
 */

const ITEM_SELECTOR = '.resource-list--grid > .resource-list__item, .product-grid > .product-grid__item';
const GRID_SELECTOR = '.resource-list--grid, .product-grid';
const PENDING_CLASS = 'wisd-card-reveal--pending';
const ACTIVE_CLASS = 'wisd-card-reveal--active';
const STAGGER_MS = 80;
const MAX_STAGGER_STEPS = 4;
/** La entrada empieza cuando la tarjeta supera este porcentaje inferior de la pantalla. */
const BOTTOM_INSET_PERCENT = 8;

/** @type {WeakSet<Element>} Ítems cuya posición inicial ya se evaluó. */
const evaluated = new WeakSet();

const revealObserver = new IntersectionObserver(handleIntersections, {
  rootMargin: `0px 0px -${BOTTOM_INSET_PERCENT}% 0px`,
});

const gridObserver = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element && isRevealable(node)) revealObserver.observe(node);
    }
  }
});

/**
 * @param {Element} item
 * @returns {boolean}
 */
function isRevealable(item) {
  return item.matches(ITEM_SELECTOR) && item.querySelector('product-card') !== null;
}

/**
 * La primera notificación de cada ítem decide su estado inicial: si ya está en pantalla
 * (o por encima) se deja visible; si está más abajo, queda oculto hasta que entre.
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersections(entries) {
  let step = 0;

  for (const entry of entries) {
    const item = entry.target;

    if (!evaluated.has(item)) {
      evaluated.add(item);
      // Borde inferior real de la pantalla, deducido del rectángulo del observer. Leer
      // window.innerHeight aquí forzaría un recálculo de layout de toda la página.
      const viewportBottom = entry.rootBounds ? entry.rootBounds.bottom / (1 - BOTTOM_INSET_PERCENT / 100) : Infinity;
      if (entry.boundingClientRect.top < viewportBottom) {
        revealObserver.unobserve(item);
      } else {
        item.classList.add(PENDING_CLASS);
      }
      continue;
    }

    if (!entry.isIntersecting) continue;

    revealObserver.unobserve(item);
    reveal(item, Math.min(step, MAX_STAGGER_STEPS) * STAGGER_MS);
    step += 1;
  }
}

/**
 * @param {Element} item
 * @param {number} delay - Retraso escalonado en milisegundos.
 */
function reveal(item, delay) {
  if (!(item instanceof HTMLElement)) return;

  item.style.setProperty('--wisd-card-reveal-delay', `${delay}ms`);
  item.classList.add(ACTIVE_CLASS);
  item.classList.remove(PENDING_CLASS);
  item.addEventListener('transitionend', finishReveal);
}

/**
 * Retira la transición al terminar para que la tarjeta quede sin estilos añadidos.
 * @param {TransitionEvent} event
 */
function finishReveal(event) {
  const item = event.currentTarget;
  if (!(item instanceof HTMLElement) || event.target !== item || event.propertyName !== 'opacity') return;

  item.removeEventListener('transitionend', finishReveal);
  item.classList.remove(ACTIVE_CLASS);
  item.style.removeProperty('--wisd-card-reveal-delay');
}

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!prefersReducedMotion && !window.Shopify?.designMode) {
  for (const item of document.querySelectorAll(ITEM_SELECTOR)) {
    if (isRevealable(item)) revealObserver.observe(item);
  }

  for (const grid of document.querySelectorAll(GRID_SELECTOR)) {
    gridObserver.observe(grid, { childList: true });
  }
}
